import React, { useState, useEffect } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Constant from "../Constant";

const EditPlanModal = ({
  open,
  onClose,
  plan,
  onPlanUpdated,
  showNotification,
}) => {
  const [formData, setFormData] = useState({
    amount: plan.amount,
    duration: plan.duration,
    description: plan.description || "",
    isActive: plan.isActive,
  });
  const [features, setFeatures] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchFeatures();
    }
  }, [open]);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      // Get all active features
      const featuresResponse = await axios.get(
        `${Constant.apiBasePath}features/active`
      );
      const allFeatures = featuresResponse.data;

      // Map current plan features
      const planFeatureMap = {};
      if (plan.planFeatures && plan.planFeatures.length > 0) {
        plan.planFeatures.forEach((pf) => {
          planFeatureMap[pf.feature._id || pf.feature] = pf.enabled;
        });
      }

      // Combine the features information
      const updatedFeatures = allFeatures.map((feature) => ({
        feature: feature._id,
        name: feature.name,
        enabled: planFeatureMap[feature._id] === true,
      }));

      setFeatures(updatedFeatures);
    } catch (error) {
      showNotification("Failed to load features", "error");
      console.error("Error loading features:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFeatureChange = (featureId, checked) => {
    setFeatures(
      features.map((feature) =>
        feature.feature === featureId
          ? { ...feature, enabled: checked }
          : feature
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Update basic plan information
      await axios.put(`${Constant.apiBasePath}plans/${plan._id}`, formData);

      // Update only changed feature statuses
      for (const feature of features) {
        const originalFeature = plan.planFeatures?.find(
          (pf) =>
            pf.feature._id === feature.feature || pf.feature === feature.feature
        );
        if (!originalFeature || originalFeature.enabled !== feature.enabled) {
          await axios.put(
            `${Constant.apiBasePath}plans/${plan._id}/features/${feature.feature}`,
            { enabled: feature.enabled }
          );
        }
      }

      showNotification("Plan updated successfully");
      onPlanUpdated();
      onClose();
    } catch (error) {
      if (error.response && error.response.data) {
        showNotification(error.response.data.message, "error");
      } else {
        showNotification("Failed to update plan", "error");
      }
      console.error("Error updating plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalStyle = {
    formGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
    },
    formGroup: {
      marginBottom: "16px",
    },
    input: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
    },
    textarea: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      gridColumn: "span 2",
    },
    label: {
      display: "block",
      marginBottom: "6px",
      fontWeight: "500",
    },
    checkboxGroup: {
      gridColumn: "span 2",
      marginBottom: "16px",
    },
    checkboxOptions: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "10px",
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      marginBottom: "8px",
    },
    buttonGroup: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "10px",
      gridColumn: "span 2",
      marginTop: "16px",
    },
    button: {
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
    },
    submitButton: {
      backgroundColor: "#4a90e2",
      color: "white",
      border: "none",
    },
    cancelButton: {
      backgroundColor: "#f5f5f5",
      border: "1px solid #ddd",
    },
    modalContent: {
      padding: "20px",
      maxWidth: "600px",
      maxHeight: "80vh",
      overflow: "auto",
    },
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      contentStyle={{ maxWidth: "600px", width: "80%" }}
      modal
      nested
    >
      <div style={modalStyle.modalContent}>
        <h2 style={{ marginBottom: "20px", textAlign: "center" }}>
          Edit Plan: {plan.name}
        </h2>
        {loading ? (
          <p style={{ textAlign: "center" }}>Loading features...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={modalStyle.formGrid}>
              <div style={modalStyle.formGroup}>
                <label style={modalStyle.label}>
                  Amount (₹):
                  <input
                    type="number"
                    step="1"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    style={modalStyle.input}
                  />
                </label>
              </div>

              <div style={modalStyle.formGroup}>
                <label style={modalStyle.label}>
                  Duration (Months):
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                    style={modalStyle.input}
                  />
                </label>
              </div>

              <div style={{ ...modalStyle.formGroup, gridColumn: "span 2" }}>
                <label style={modalStyle.label}>
                  Description:
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    style={modalStyle.textarea}
                  />
                </label>
              </div>

              <div style={modalStyle.checkboxGroup}>
                <label style={modalStyle.label}>Features:</label>
                <div style={modalStyle.checkboxOptions}>
                  {features.map((feature) => (
                    <label
                      key={feature.feature}
                      style={modalStyle.checkboxLabel}
                    >
                      <input
                        type="checkbox"
                        checked={feature.enabled}
                        onChange={(e) =>
                          handleFeatureChange(feature.feature, e.target.checked)
                        }
                        style={{ marginRight: "8px" }}
                      />
                      {feature.name}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ ...modalStyle.formGroup, gridColumn: "span 2" }}>
                <label
                  style={{ ...modalStyle.checkboxLabel, marginBottom: "0" }}
                >
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    style={{ marginRight: "8px" }}
                  />
                  Active
                </label>
              </div>

              <div style={modalStyle.buttonGroup}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{ ...modalStyle.button, ...modalStyle.cancelButton }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{ ...modalStyle.button, ...modalStyle.submitButton }}
                >
                  {isSubmitting ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Popup>
  );
};

export default EditPlanModal;
